import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { interval, Observable, Subject } from 'rxjs';
import { SnackService } from '../snack.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { UpdateSnackComponent } from './snack/update-snack.component';
import { filter, map, takeUntil } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CheckForUpdateService {
  private versionUpdated$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly updates: SwUpdate,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
    private readonly snackService: SnackService,
  ) {}

  private static majorChange(current: string, available: string): boolean {
    return available.split('.')[0] > current.split('.')[0];
  }

  private static minorChange(current: string, available: string): boolean {
    return available.split('.')[1] > current.split('.')[1];
  }

  init(): void {
    const every15m$ = interval(15 * 60 * 1000);
    every15m$.subscribe(() => this.updates.checkForUpdate());
    this.listenOnUpdates();

    this.updates.unrecoverable.subscribe((event) => {
      this.snackService.snackThis('commons.messages.update.unrecoverable', null, event);
    });
  }

  private listenOnUpdates(): void {
    this.updates.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        map((evt) => ({ current: evt.currentVersion, available: evt.latestVersion })),
      )
      .subscribe((update) => {
        console.log('Update available:', update);

        const { version: currentVersion = '0.0.0' } = update.current.appData as { version: string };
        const { version: availableVersion } = update.available.appData as { version: string };

        if (+availableVersion.replace('.', '') <= +currentVersion.replace('.', '')) {
          console.log('Available version is the same or older, ignoring the update.', update);
        } else if (CheckForUpdateService.majorChange(currentVersion, availableVersion) || CheckForUpdateService.minorChange(currentVersion, availableVersion)) {
          this.update();
        } else {
          this.versionUpdated$.next(true);
          const snack = this.snackBar.openFromComponent(UpdateSnackComponent, { duration: undefined });
          snack.onAction().pipe(takeUntil(snack.afterDismissed())).subscribe(() => this.update());
        }
      });
  }

  versionUpdated(): Observable<boolean> {
    return this.versionUpdated$.asObservable();
  }

  update(): void {
    this.updates
      .activateUpdate()
      .catch(console.error)
      .finally(() => document.location.reload());
  }
}
